import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SingleVerifier from '../components/SingleVerifier';
import BulkVerifier from '../components/BulkVerifier';
import VerificationChart from '../components/VerificationChart';
import DomainBreakdown from '../components/DomainBreakdown';
import SessionTimeout from '../components/SessionTimeout';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const C = {
  green: '#3cb74e', black: '#111111', white: '#ffffff',
  red: '#ef4444', amber: '#f59e0b', gray: '#64748b',
  border: '#e2e8f0', lightBg: '#f8fdf8',
};
const statusColors = {
  valid:   { bg: '#dcfce7', text: '#166534' },
  invalid: { bg: '#fee2e2', text: '#991b1b' },
  risky:   { bg: '#fef3c7', text: '#92400e' },
  unknown: { bg: '#f1f5f9', text: '#475569' },
};
const statusIcons = { valid: '✅', invalid: '❌', risky: '⚠️', unknown: '❓' };

export default function DashboardPage() {
  const [tab, setTab]                     = useState('single');
  // All Results mode
  const [history, setHistory]             = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyPage, setHistoryPage]     = useState(1);
  const [historyTotal, setHistoryTotal]   = useState(0);
  const [historyCounts, setHistoryCounts] = useState({});
  const [historySearch, setHistorySearch] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  // By Date mode
  const [historyMode, setHistoryMode]     = useState('all');
  const [dailyList, setDailyList]         = useState([]);
  const [selectedDate, setSelectedDate]   = useState(null);
  const [dateRows, setDateRows]           = useState([]);
  const [dateTotal, setDateTotal]         = useState(0);
  const [datePage, setDatePage]           = useState(1);
  const [dateFilter, setDateFilter]       = useState('all');
  const [dateLoading, setDateLoading]     = useState(false);

  const { user, logout } = useAuth();
  const navigate         = useNavigate();

  const usedTotal   = user?.emails_used || 0;
  const limitTotal  = (user?.email_limit || 1000) + (user?.credits_topup || 0);
  const remaining   = limitTotal - usedTotal;
  const usedPct     = Math.min((usedTotal / Math.max(limitTotal, 1)) * 100, 100);
  const isWarning   = usedPct >= 80;
  const isCritical  = usedPct >= 95;
  const isExhausted = remaining <= 0;

  useEffect(() => {
    if (tab === 'history') {
      if (historyMode === 'all') fetchHistory();
      else fetchDailyList();
    }
  }, [tab, historyFilter, historyPage, historyMode]);

  useEffect(() => {
    if (selectedDate) fetchDateRows();
  }, [selectedDate, datePage, dateFilter]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/email/history?page=${historyPage}&limit=20&status=${historyFilter}`);
      setHistory(res.data.data || []);
      setHistoryTotal(res.data.pagination.total);
      if (!Object.keys(historyCounts).length) fetchHistoryCounts();
    } catch {} finally { setHistoryLoading(false); }
  };

  const fetchHistoryCounts = async () => {
    try {
      const statuses = ['valid', 'invalid', 'risky', 'unknown'];
      const results  = await Promise.all(statuses.map(s => api.get(`/email/history?page=1&limit=1&status=${s}`)));
      const counts   = {};
      statuses.forEach((s, i) => { counts[s] = results[i].data.pagination.total; });
      setHistoryCounts(counts);
    } catch {}
  };

  const fetchDailyList = async () => {
    try {
      const res = await api.get('/email/history/by-date');
      // Normalise date field to plain YYYY-MM-DD string
      const normalised = (res.data.data || []).map(d => {
        // d.date may be a Date object or ISO string — always extract YYYY-MM-DD
        const raw = d.date;
        let dateStr;
        if (raw instanceof Date) {
          dateStr = raw.toISOString().slice(0, 10);
        } else if (typeof raw === 'string' && raw.includes('T')) {
          dateStr = raw.slice(0, 10);
        } else {
          dateStr = String(raw).slice(0, 10);
        }
        return { ...d, date: dateStr };
      });
      setDailyList(normalised);
      if (!Object.keys(historyCounts).length) fetchHistoryCounts();
    } catch {}
  };

  const fetchDateRows = async () => {
    if (!selectedDate) return;
    setDateLoading(true);
    try {
      const params = new URLSearchParams({ page: datePage });
      if (dateFilter !== 'all') params.set('status', dateFilter);
      const res = await api.get(`/email/history/date/${selectedDate}?${params}`);
      setDateRows(res.data.data || []);
      setDateTotal(res.data.pagination.total);
    } catch {} finally { setDateLoading(false); }
  };

  const handleHistoryExport = async (statusFilter = null) => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/export/csv${params.toString() ? '?' + params : ''}`, { responseType: 'blob' });
      const label = statusFilter && statusFilter !== 'all' ? statusFilter : 'all';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = `history_${label}.csv`;
      a.click();
    } catch { alert('Export failed'); }
  };

  const handleDateExport = async (date, statusFilter = null) => {
    try {
      // Robustly extract YYYY-MM-DD regardless of input type
      let safeDate;
      if (date instanceof Date) {
        safeDate = date.toISOString().slice(0, 10);
      } else if (typeof date === 'string' && date.includes('T')) {
        safeDate = date.slice(0, 10);
      } else {
        safeDate = String(date).slice(0, 10);
      }
      const params = new URLSearchParams({ date: safeDate });
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/export/csv?${params}`, { responseType: 'blob' });
      const label = statusFilter && statusFilter !== 'all' ? statusFilter : 'all';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = `history_${date}_${label}.csv`;
      a.click();
    } catch { alert('Export failed'); }
  };

  const filteredHistory = historySearch
    ? history.filter(r => r.email.toLowerCase().includes(historySearch.toLowerCase()))
    : history;

  const totalHistoryAll = Object.values(historyCounts).reduce((a, b) => a + b, 0) || historyTotal;

  const navItems = [
    { key: 'single',    icon: '🔍', label: 'Single Verify' },
    { key: 'bulk',      icon: '📂', label: 'Bulk Verify'   },
    { key: 'analytics', icon: '📊', label: 'Analytics'     },
    { key: 'history',   icon: '📋', label: 'History'       },
  ];

  return (
    <div style={s.page}>
      <SessionTimeout />

      {/* ── Sidebar ── */}
      <div style={s.sidebar}>
        <div style={s.brand}>
          <span style={s.brandIcon}>✉️</span>
          <div>
            <p style={s.brandName}>EmailVerifier</p>
            <p style={s.brandSub}>msdiscoveries.com</p>
          </div>
        </div>

        <div style={s.clientCard}>
          <div style={s.clientAvatar}>{user?.name?.[0]?.toUpperCase()}</div>
          <div>
            <p style={s.clientName}>{user?.name}</p>
            <p style={s.clientCompany}>{user?.company}</p>
            <p style={s.clientPhone}>📱 {user?.phone}</p>
          </div>
        </div>

        <div style={{ ...s.usageBox, border: isCritical ? '1.5px solid #ef4444' : isWarning ? '1.5px solid #f59e0b' : '1.5px solid rgba(255,255,255,0.12)' }}>
          <div style={s.usageHeader}>
            <span style={s.usageLabel}>Email Usage</span>
            <span style={{ ...s.usageCount, color: isCritical ? '#fca5a5' : isWarning ? '#fde68a' : C.white }}>
              {usedTotal}/{limitTotal}
            </span>
          </div>
          <div style={s.usageTrack}>
            <div style={{ ...s.usageFill, width: `${usedPct}%`, background: isCritical ? C.red : isWarning ? C.amber : C.green }} />
          </div>
          <p style={s.usagePct}>{usedPct.toFixed(1)}% used · {remaining} remaining</p>
          {isWarning && !isCritical  && <p style={s.usageWarnAmber}>⚠️ Nearing your limit</p>}
          {isCritical && !isExhausted && <p style={s.usageWarnRed}>🚨 Almost at limit! Contact admin</p>}
          {isExhausted                && <p style={s.usageWarnRed}>🔴 Limit reached! Contact admin</p>}
        </div>

        <nav style={s.nav}>
          {navItems.map(({ key, icon, label }) => (
            <button key={key}
              style={{ ...s.navBtn, ...(tab === key ? s.navBtnActive : {}) }}
              onClick={() => setTab(key)}>
              <span>{icon}</span><span>{label}</span>
            </button>
          ))}
        </nav>
        <button style={s.logoutBtn} onClick={() => { logout(); navigate('/login'); }}>🚪 Logout</button>
      </div>

      {/* ── Main ── */}
      <div style={s.main}>

        {/* Credit banners */}
        {isExhausted && (
          <div style={s.bannerRed}>
            <span style={s.bannerIcon}>🔴</span>
            <div>
              <p style={s.bannerTitle}>Verification limit reached!</p>
              <p style={s.bannerSub}>You have used all {limitTotal} credits. Contact admin to top up.</p>
            </div>
          </div>
        )}
        {isCritical && !isExhausted && (
          <div style={s.bannerRed}>
            <span style={s.bannerIcon}>🚨</span>
            <div>
              <p style={s.bannerTitle}>Almost out of credits — only {remaining} left!</p>
              <p style={s.bannerSub}>You've used {usedPct.toFixed(0)}% of your limit. Contact admin to top up.</p>
            </div>
          </div>
        )}
        {isWarning && !isCritical && (
          <div style={s.bannerAmber}>
            <span style={s.bannerIcon}>⚠️</span>
            <div>
              <p style={{ ...s.bannerTitle, color: '#92400e' }}>Credit warning — {remaining} remaining</p>
              <p style={{ ...s.bannerSub, color: '#a16207' }}>You've used {usedPct.toFixed(0)}% of your limit.</p>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div style={s.topBar}>
          <div>
            <h1 style={s.pageTitle}>
              {tab === 'single'    && '🔍 Single Email Verification'}
              {tab === 'bulk'      && '📂 Bulk Email Verification'}
              {tab === 'analytics' && '📊 Analytics & Insights'}
              {tab === 'history'   && '📋 Verification History'}
            </h1>
            <p style={s.pageSub}>
              {tab === 'single'    && 'Verify one email with full format, MX, SMTP & catch-all check'}
              {tab === 'bulk'      && 'Upload CSV — auto removes duplicates, shows batch-by-batch progress'}
              {tab === 'analytics' && 'Trends, domain breakdown, bounce estimates & top invalid domains'}
              {tab === 'history'   && 'All past results — filter, search and download by status or by date'}
            </p>
          </div>
          <div style={s.quickStats}>
            {[
              { label: 'Used',      value: usedTotal,  color: C.black },
              { label: 'Remaining', value: remaining,   color: remaining <= 0 ? C.red : C.green },
              { label: 'Limit',     value: limitTotal,  color: C.gray },
            ].map(({ label, value, color }) => (
              <div key={label} style={s.quickStat}>
                <p style={{ ...s.quickNum, color }}>{value}</p>
                <p style={s.quickLabel}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Tab Content ── */}
        {tab === 'single'    && <SingleVerifier />}
        {tab === 'bulk'      && <BulkVerifier />}
        {tab === 'analytics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <BounceEstimator />
            <VerificationChart />
            <DomainBreakdown />
          </div>
        )}

        {/* ── History Tab ── */}
        {tab === 'history' && (
          <div>

            {/* Mode toggle */}
            <div style={s.modeRow}>
              <button
                style={{ ...s.modeBtn, ...(historyMode === 'all'    ? s.modeBtnActive : {}) }}
                onClick={() => { setHistoryMode('all'); setSelectedDate(null); }}>
                📋 All Results
              </button>
              <button
                style={{ ...s.modeBtn, ...(historyMode === 'bydate' ? s.modeBtnActive : {}) }}
                onClick={() => { setHistoryMode('bydate'); setSelectedDate(null); fetchDailyList(); }}>
                📅 By Date
              </button>
            </div>

            {/* ════════════════ BY DATE MODE ════════════════ */}
            {historyMode === 'bydate' && (
              <div>
                {/* Day list */}
                {!selectedDate && (
                  <div>
                    <p style={s.byDateHint}>Click a day to view its results, or use the download buttons to export directly.</p>
                    {dailyList.length === 0 ? (
                      <div style={s.histEmpty}>
                        <p style={{ fontSize: 32, margin: '0 0 8px' }}>📭</p>
                        <p>No verifications yet.</p>
                      </div>
                    ) : (
                      <div style={s.dayGrid}>
                        {dailyList.map((d, i) => {
                          const dateLabel = new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
                          const validPct  = d.total > 0 ? Math.round((d.valid / d.total) * 100) : 0;
                          const pctColor  = validPct >= 70 ? C.green : validPct >= 40 ? C.amber : C.red;
                          return (
                            <div key={i} style={s.dayCard}
                              onClick={() => {
                                  const raw = d.date;
                                  const ds = raw instanceof Date ? raw.toISOString().slice(0,10) : String(raw).includes('T') ? String(raw).slice(0,10) : String(raw).slice(0,10);
                                  setSelectedDate(ds); setDatePage(1); setDateFilter('all');
                                }}>
                              <div style={s.dayCardTop}>
                                <div>
                                  <p style={s.dayDate}>📅 {dateLabel}</p>
                                  <p style={s.dayTotal}>{d.total} emails verified</p>
                                </div>
                                <div style={{ ...s.dayValidPct, color: pctColor }}>
                                  {validPct}%<br />
                                  <span style={{ fontSize: 10, fontWeight: 400 }}>valid</span>
                                </div>
                              </div>
                              {/* Stacked bar */}
                              <div style={s.dayBarTrack}>
                                <div style={{ ...s.dayBarSeg, width: `${(d.valid / d.total) * 100}%`,   background: C.green }} />
                                <div style={{ ...s.dayBarSeg, width: `${(d.invalid / d.total) * 100}%`, background: C.red   }} />
                                <div style={{ ...s.dayBarSeg, width: `${(d.risky / d.total) * 100}%`,   background: C.amber }} />
                                <div style={{ ...s.dayBarSeg, width: `${(d.unknown / d.total) * 100}%`, background: C.gray  }} />
                              </div>
                              <div style={s.dayCountRow}>
                                <span style={{ color: C.green, fontSize: 11, fontWeight: 600 }}>✅ {d.valid}</span>
                                <span style={{ color: C.red,   fontSize: 11, fontWeight: 600 }}>❌ {d.invalid}</span>
                                <span style={{ color: C.amber, fontSize: 11, fontWeight: 600 }}>⚠️ {d.risky}</span>
                                <span style={{ color: C.gray,  fontSize: 11, fontWeight: 600 }}>❓ {d.unknown}</span>
                              </div>
                              {/* Download buttons — stop click from opening drill-down */}
                              <div style={s.dayCardActions} onClick={e => e.stopPropagation()}>
                                <button style={s.dayDlBtn(C.black)}  onClick={() => handleDateExport(d.date, null)}>⬇️ All</button>
                                <button style={s.dayDlBtn(C.green)}  onClick={() => handleDateExport(d.date, 'valid')}>✅ Valid</button>
                                <button style={s.dayDlBtn(C.red)}    onClick={() => handleDateExport(d.date, 'invalid')}>❌ Invalid</button>
                                <button style={s.dayDlBtn(C.amber)}  onClick={() => handleDateExport(d.date, 'risky')}>⚠️ Risky</button>
                                <button style={s.dayDlBtn(C.gray)}   onClick={() => handleDateExport(d.date, 'unknown')}>❓ Unknown</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Drill-down for selected date */}
                {selectedDate && (
                  <div>
                    <div style={s.dateBackRow}>
                      <button style={s.backBtn} onClick={() => setSelectedDate(null)}>← Back to dates</button>
                      <p style={s.dateTitle}>
                        📅 {new Date(selectedDate).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                      </p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button style={s.dayDlBtn(C.black)}  onClick={() => handleDateExport(selectedDate, null)}>⬇️ All</button>
                        <button style={s.dayDlBtn(C.green)}  onClick={() => handleDateExport(selectedDate, 'valid')}>✅ Valid</button>
                        <button style={s.dayDlBtn(C.red)}    onClick={() => handleDateExport(selectedDate, 'invalid')}>❌ Invalid</button>
                        <button style={s.dayDlBtn(C.amber)}  onClick={() => handleDateExport(selectedDate, 'risky')}>⚠️ Risky</button>
                      </div>
                    </div>

                    {/* Status filter */}
                    <div style={{ ...s.histControls, marginTop: 12 }}>
                      <div style={s.histFilterTabs}>
                        {['all', 'valid', 'invalid', 'risky', 'unknown'].map(f => {
                          const sc = statusColors[f];
                          const isActive = dateFilter === f;
                          return (
                            <button key={f}
                              style={{
                                ...s.histTab,
                                ...(isActive ? {
                                  background: f === 'all' ? C.green : sc.bg,
                                  color:      f === 'all' ? C.white : sc.text,
                                  border:     `1.5px solid ${f === 'all' ? C.green : sc.text}`,
                                  fontWeight: 700,
                                } : {}),
                              }}
                              onClick={() => { setDateFilter(f); setDatePage(1); }}>
                              {f === 'all' ? 'All' : `${statusIcons[f]} ${f.charAt(0).toUpperCase() + f.slice(1)}`}
                            </button>
                          );
                        })}
                      </div>
                      <p style={s.pageInfo}>{dateTotal} results</p>
                    </div>

                    <div style={s.histTableBox}>
                      {dateLoading ? (
                        <div style={s.histLoading}>Loading...</div>
                      ) : dateRows.length === 0 ? (
                        <div style={s.histEmpty}>
                          <p style={{ fontSize: 28 }}>📭</p><p>No results.</p>
                        </div>
                      ) : (
                        <>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={s.table}>
                              <thead>
                                <tr style={s.theadRow}>
                                  {['#', 'Email', 'Format', 'MX Record', 'SMTP', 'Catch-all', 'Disposable', 'Status', 'Time'].map(h => (
                                    <th key={h} style={s.th}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {dateRows.map((r, i) => (
                                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.lightBg }}>
                                    <td style={s.tdNum}>{(datePage - 1) * 20 + i + 1}</td>
                                    <td style={s.tdEmail}>{r.email}</td>
                                    <td style={s.tdC}>{r.format_valid  ? '✅' : '❌'}</td>
                                    <td style={s.tdC}>{r.mx_valid      ? '✅' : '❌'}</td>
                                    <td style={s.tdC}>{r.smtp_valid    ? '✅' : '❌'}</td>
                                    <td style={s.tdC}>{r.is_catchall   ? <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>⚠️ Yes</span> : '—'}</td>
                                    <td style={s.tdC}>{r.is_disposable ? <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>⚠️ Yes</span> : '—'}</td>
                                    <td style={s.tdC}>
                                      <span style={{ ...s.statusBadge, background: statusColors[r.status]?.bg, color: statusColors[r.status]?.text }}>
                                        {statusIcons[r.status]} {r.status}
                                      </span>
                                    </td>
                                    <td style={s.tdDate}>{new Date(r.verified_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {dateTotal > 20 && (
                            <div style={s.histPagination}>
                              <span style={s.pageInfo}>Showing {(datePage - 1) * 20 + 1}–{Math.min(datePage * 20, dateTotal)} of {dateTotal}</span>
                              <div style={s.pageBtns}>
                                <button style={s.pageBtn} disabled={datePage === 1} onClick={() => setDatePage(p => p - 1)}>← Prev</button>
                                <button style={s.pageBtn} disabled={datePage >= Math.ceil(dateTotal / 20)} onClick={() => setDatePage(p => p + 1)}>Next →</button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ════════════════ ALL RESULTS MODE ════════════════ */}
            {historyMode === 'all' && (
              <div>
                {/* Summary cards */}
                <div style={s.histSummary}>
                  {[
                    { label: 'Total',   value: totalHistoryAll,            color: C.black, icon: '📧' },
                    { label: 'Valid',   value: historyCounts.valid   || 0, color: C.green, icon: '✅' },
                    { label: 'Invalid', value: historyCounts.invalid || 0, color: C.red,   icon: '❌' },
                    { label: 'Risky',   value: historyCounts.risky   || 0, color: C.amber, icon: '⚠️' },
                    { label: 'Unknown', value: historyCounts.unknown || 0, color: C.gray,  icon: '❓' },
                  ].map(({ label, value, color, icon }) => (
                    <div key={label} style={s.histSummaryCard}>
                      <p style={s.histSummaryIcon}>{icon}</p>
                      <p style={{ ...s.histSummaryNum, color }}>{value}</p>
                      <p style={s.histSummaryLabel}>{label}</p>
                    </div>
                  ))}
                </div>

                {/* Download by status */}
                <div style={s.histDownloadRow}>
                  <p style={s.histDownloadLabel}>⬇️ Download by status:</p>
                  <div style={s.histDownloadBtns}>
                    <button style={s.dlBtn(C.black)} onClick={() => handleHistoryExport(null)}>All ({totalHistoryAll})</button>
                    <button style={s.dlBtn(C.green)} onClick={() => handleHistoryExport('valid')}>✅ Valid ({historyCounts.valid || 0})</button>
                    <button style={s.dlBtn(C.red)}   onClick={() => handleHistoryExport('invalid')}>❌ Invalid ({historyCounts.invalid || 0})</button>
                    <button style={s.dlBtn(C.amber)} onClick={() => handleHistoryExport('risky')}>⚠️ Risky ({historyCounts.risky || 0})</button>
                    <button style={s.dlBtn(C.gray)}  onClick={() => handleHistoryExport('unknown')}>❓ Unknown ({historyCounts.unknown || 0})</button>
                  </div>
                </div>

                {/* Filter tabs + search */}
                <div style={s.histControls}>
                  <div style={s.histFilterTabs}>
                    {['all', 'valid', 'invalid', 'risky', 'unknown'].map(f => {
                      const isActive = historyFilter === f;
                      const sc = statusColors[f];
                      const count = f === 'all' ? totalHistoryAll : (historyCounts[f] || 0);
                      return (
                        <button key={f}
                          style={{
                            ...s.histTab,
                            ...(isActive ? {
                              background: f === 'all' ? C.green : sc.bg,
                              color:      f === 'all' ? C.white : sc.text,
                              border:     `1.5px solid ${f === 'all' ? C.green : sc.text}`,
                              fontWeight: 700,
                            } : {}),
                          }}
                          onClick={() => { setHistoryFilter(f); setHistoryPage(1); }}>
                          {f === 'all' ? `All (${count})` : `${statusIcons[f]} ${f.charAt(0).toUpperCase() + f.slice(1)} (${count})`}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    style={s.histSearch}
                    placeholder="🔍 Search email..."
                    value={historySearch}
                    onChange={e => setHistorySearch(e.target.value)}
                  />
                </div>

                {/* Table */}
                <div style={s.histTableBox}>
                  {historyLoading ? (
                    <div style={s.histLoading}>Loading...</div>
                  ) : filteredHistory.length === 0 ? (
                    <div style={s.histEmpty}>
                      <p style={{ fontSize: 32, margin: '0 0 8px' }}>📭</p>
                      <p>No verifications found.</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={s.table}>
                          <thead>
                            <tr style={s.theadRow}>
                              {['#', 'Email', 'Format', 'MX Record', 'SMTP', 'Catch-all', 'Disposable', 'Status', 'Date'].map(h => (
                                <th key={h} style={s.th}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredHistory.map((r, i) => (
                              <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.lightBg }}>
                                <td style={s.tdNum}>{(historyPage - 1) * 20 + i + 1}</td>
                                <td style={s.tdEmail}>{r.email}</td>
                                <td style={s.tdC}>{r.format_valid  ? '✅' : '❌'}</td>
                                <td style={s.tdC}>{r.mx_valid      ? '✅' : '❌'}</td>
                                <td style={s.tdC}>{r.smtp_valid    ? '✅' : '❌'}</td>
                                <td style={s.tdC}>{r.is_catchall   ? <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>⚠️ Yes</span> : '—'}</td>
                                <td style={s.tdC}>{r.is_disposable ? <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>⚠️ Yes</span> : '—'}</td>
                                <td style={s.tdC}>
                                  <span style={{ ...s.statusBadge, background: statusColors[r.status]?.bg, color: statusColors[r.status]?.text }}>
                                    {statusIcons[r.status]} {r.status}
                                  </span>
                                </td>
                                <td style={s.tdDate}>{new Date(r.verified_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {historyTotal > 20 && (
                        <div style={s.histPagination}>
                          <span style={s.pageInfo}>
                            Showing {(historyPage - 1) * 20 + 1}–{Math.min(historyPage * 20, historyTotal)} of {historyTotal}
                          </span>
                          <div style={s.pageBtns}>
                            <button style={s.pageBtn} disabled={historyPage === 1} onClick={() => setHistoryPage(p => p - 1)}>← Prev</button>
                            {Array.from({ length: Math.min(5, Math.ceil(historyTotal / 20)) }, (_, i) => {
                              const p = Math.max(1, Math.min(historyPage - 2, Math.ceil(historyTotal / 20) - 4)) + i;
                              return (
                                <button key={p}
                                  style={{ ...s.pageBtn, ...(p === historyPage ? { background: C.green, color: C.white, border: `1.5px solid ${C.green}` } : {}) }}
                                  onClick={() => setHistoryPage(p)}>{p}
                                </button>
                              );
                            })}
                            <button style={s.pageBtn} disabled={historyPage >= Math.ceil(historyTotal / 20)} onClick={() => setHistoryPage(p => p + 1)}>Next →</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

function BounceEstimator() {
  const [stats, setStats] = useState(null);
  const colors = { red: '#ef4444', amber: '#f59e0b', green: '#3cb74e' };
  useEffect(() => {
    api.get('/email/stats/domains').then(r => {
      const data = r.data.data || [];
      const total   = data.reduce((a, b) => a + parseInt(b.total), 0);
      const invalid = data.reduce((a, b) => a + parseInt(b.invalid), 0);
      setStats({ total, bounceRate: total > 0 ? ((invalid / total) * 100).toFixed(1) : 0 });
    }).catch(() => {});
  }, []);
  if (!stats || stats.total === 0) return null;
  const color = stats.bounceRate > 20 ? colors.red : stats.bounceRate > 10 ? colors.amber : colors.green;
  return (
    <div style={{ background: '#ffffff', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: 20, display: 'flex', alignItems: 'center', gap: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div style={{ fontSize: 48 }}>📊</div>
      <div style={{ flex: 1 }}>
        <p style={{ color: '#111111', fontWeight: 700, fontSize: 15, margin: '0 0 4px' }}>Estimated Bounce Rate</p>
        <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 10px' }}>Based on {stats.total} verified emails</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 36, fontWeight: 800, color }}>{stats.bounceRate}%</span>
          <span style={{ background: color + '20', color, padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
            {stats.bounceRate > 20 ? '🚨 High Risk' : stats.bounceRate > 10 ? '⚠️ Moderate' : '✅ Good'}
          </span>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', background: '#f8fdf8' },
  sidebar:    { width: 240, background: '#111111', padding: '20px 14px', display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0, minHeight: '100vh' },
  brand:      { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.1)' },
  brandIcon:  { fontSize: 28 },
  brandName:  { color: '#3cb74e', fontWeight: 800, fontSize: 16, margin: 0 },
  brandSub:   { color: 'rgba(255,255,255,0.4)', fontSize: 10, margin: 0 },
  clientCard: { background: 'rgba(255,255,255,0.07)', borderRadius: 10, padding: 12, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' },
  clientAvatar: { width: 36, height: 36, borderRadius: '50%', background: '#3cb74e', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0 },
  clientName:   { color: '#ffffff', fontWeight: 700, fontSize: 13, margin: '0 0 2px' },
  clientCompany:{ color: 'rgba(255,255,255,0.55)', fontSize: 11, margin: '0 0 2px' },
  clientPhone:  { color: 'rgba(255,255,255,0.35)', fontSize: 11, margin: 0 },
  usageBox:   { background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: 12, marginBottom: 14 },
  usageHeader:{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 },
  usageLabel: { color: 'rgba(255,255,255,0.55)', fontSize: 11 },
  usageCount: { fontSize: 11, fontWeight: 700 },
  usageTrack: { height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden', marginBottom: 4 },
  usageFill:  { height: '100%', borderRadius: 3, transition: 'width 0.4s' },
  usagePct:   { color: 'rgba(255,255,255,0.4)', fontSize: 10, margin: 0 },
  usageWarnAmber: { color: '#fde68a', fontSize: 11, margin: '6px 0 0', fontWeight: 600, padding: '5px 8px', background: 'rgba(245,158,11,0.15)', borderRadius: 6, borderLeft: '3px solid #f59e0b' },
  usageWarnRed:   { color: '#fca5a5', fontSize: 11, margin: '6px 0 0', fontWeight: 600, padding: '5px 8px', background: 'rgba(239,68,68,0.15)', borderRadius: 6, borderLeft: '3px solid #ef4444' },
  nav:        { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  navBtn:     { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 500 },
  navBtnActive:{ background: '#3cb74e', color: '#ffffff', fontWeight: 700 },
  logoutBtn:  { padding: '10px 12px', borderRadius: 8, border: 'none', background: 'rgba(239,68,68,0.2)', color: '#fca5a5', cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 600, marginTop: 8 },
  main:       { flex: 1, padding: 28, overflowY: 'auto' },
  bannerRed:  { display: 'flex', gap: 14, background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '14px 18px', marginBottom: 16, alignItems: 'flex-start' },
  bannerAmber:{ display: 'flex', gap: 14, background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 12, padding: '14px 18px', marginBottom: 16, alignItems: 'flex-start' },
  bannerIcon: { fontSize: 22, flexShrink: 0 },
  bannerTitle:{ color: '#991b1b', fontWeight: 700, fontSize: 14, margin: '0 0 4px' },
  bannerSub:  { color: '#b91c1c', fontSize: 13, margin: 0 },
  topBar:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 },
  pageTitle:  { color: '#111111', fontSize: 22, fontWeight: 800, margin: '0 0 4px' },
  pageSub:    { color: '#64748b', fontSize: 13, margin: 0 },
  quickStats: { display: 'flex', gap: 12 },
  quickStat:  { background: '#ffffff', borderRadius: 12, padding: '12px 20px', textAlign: 'center', border: '1.5px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  quickNum:   { fontSize: 24, fontWeight: 800, margin: 0 },
  quickLabel: { color: '#64748b', fontSize: 11, margin: '2px 0 0' },
  // Mode toggle
  modeRow:       { display: 'flex', gap: 8, marginBottom: 18 },
  modeBtn:       { padding: '9px 22px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: '#ffffff', color: '#64748b', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  modeBtnActive: { background: '#3cb74e', color: '#ffffff', border: '1.5px solid #3cb74e', fontWeight: 700 },
  byDateHint:    { color: '#64748b', fontSize: 13, marginBottom: 16 },
  // Day grid
  dayGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14, marginBottom: 20 },
  dayCard:      { background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' },
  dayCardTop:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  dayDate:      { color: '#111111', fontWeight: 700, fontSize: 14, margin: '0 0 3px' },
  dayTotal:     { color: '#64748b', fontSize: 12, margin: 0 },
  dayValidPct:  { fontSize: 22, fontWeight: 800, textAlign: 'right', lineHeight: 1.2 },
  dayBarTrack:  { display: 'flex', height: 7, borderRadius: 4, overflow: 'hidden', marginBottom: 10, background: '#f1f5f9' },
  dayBarSeg:    { height: '100%' },
  dayCountRow:  { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  dayCardActions:{ display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 10 },
  dayDlBtn:     (bg) => ({ padding: '5px 11px', background: bg, color: '#ffffff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 11 }),
  dateBackRow:  { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
  dateTitle:    { color: '#111111', fontWeight: 700, fontSize: 15, flex: 1, margin: 0 },
  backBtn:      { padding: '8px 16px', background: '#f1f5f9', color: '#111111', border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  // History
  histSummary:     { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  histSummaryCard: { flex: 1, minWidth: 90, background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '14px 10px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  histSummaryIcon: { fontSize: 20, margin: '0 0 4px' },
  histSummaryNum:  { fontSize: 24, fontWeight: 800, margin: 0 },
  histSummaryLabel:{ color: '#64748b', fontSize: 11, margin: '3px 0 0' },
  histDownloadRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' },
  histDownloadLabel:{ color: '#111111', fontSize: 13, fontWeight: 600, margin: 0 },
  histDownloadBtns:{ display: 'flex', gap: 8, flexWrap: 'wrap' },
  dlBtn:        (bg) => ({ padding: '7px 14px', background: bg, color: '#ffffff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 12 }),
  histControls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' },
  histFilterTabs:{ display: 'flex', gap: 6, flexWrap: 'wrap' },
  histTab:      { padding: '7px 14px', borderRadius: 20, border: '1.5px solid #e2e8f0', background: '#ffffff', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  histSearch:   { padding: '9px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', color: '#111111', background: '#ffffff', width: 220 },
  histTableBox: { background: '#ffffff', borderRadius: 14, border: '1.5px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  histLoading:  { textAlign: 'center', padding: 60, color: '#64748b', fontSize: 14 },
  histEmpty:    { textAlign: 'center', padding: 60, color: '#64748b', fontSize: 13 },
  histPagination:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e2e8f0' },
  pageInfo:     { color: '#64748b', fontSize: 13 },
  pageBtns:     { display: 'flex', gap: 4 },
  pageBtn:      { padding: '6px 12px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#ffffff', color: '#111111', cursor: 'pointer', fontSize: 13 },
  table:        { width: '100%', borderCollapse: 'collapse' },
  theadRow:     { background: '#111111' },
  th:           { padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#ffffff', whiteSpace: 'nowrap' },
  tdNum:        { padding: '10px 14px', color: '#64748b', fontSize: 12, minWidth: 40 },
  tdEmail:      { padding: '10px 14px', color: '#111111', fontSize: 13, fontWeight: 500 },
  tdC:          { padding: '10px 14px', textAlign: 'center', fontSize: 13 },
  tdDate:       { padding: '10px 14px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' },
  statusBadge:  { padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, textTransform: 'capitalize', display: 'inline-block' },
};
